import { ApiResponse } from '../types';
import { CarInput, CarResponse } from '../types/usedCars';
import { CouponAssessmentInput, CouponAssessmentResponse } from '../types/coupons';
import { BankMarketingInput, BankMarketingResponse } from '../types/bankMarketing';
import { API_URL, logger } from '../utils/config';

// Base URL for API calls
const API_BASE_URL = API_URL;

// Generic fetch function with error handling
async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  try {
    const url = `${API_BASE_URL}${endpoint}`;

    logger.info(`API Request: ${options.method || 'GET'} ${url}`);

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      logger.error(`API Error: ${response.status} ${response.statusText}`, data);
      return {
        error: data.detail || 'An error occurred',
        status: 'error',
        timestamp: new Date(),
      };
    }

    logger.info(`API Success: ${response.status} ${response.statusText}`, { endpoint });
    return {
      data,
      status: 'success',
      timestamp: new Date(),
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    logger.error(`API Exception: ${errorMessage}`, { endpoint });
    return {
      error: errorMessage,
      status: 'error',
      timestamp: new Date(),
    };
  }
}

// API functions
export const api = {
  // Health check
  getHealth: () => fetchApi<{ status: string }>('/health'),

  // Used Cars
  predictCarPrice: (carInput: CarInput) =>
    fetchApi<CarResponse>('/used_cars/predict', {
      method: 'POST',
      body: JSON.stringify(carInput),
    }),

  // Coupon Assessment
  predictCouponAcceptance: (couponInput: CouponAssessmentInput) =>
    fetchApi<CouponAssessmentResponse>('/coupon_assessment/predict', {
      method: 'POST',
      body: JSON.stringify(couponInput),
    }),

  // Bank Marketing
  predictBankSubscription: (bankInput: BankMarketingInput) =>
    fetchApi<BankMarketingResponse>('/bank_marketing/predict', {
      method: 'POST',
      body: JSON.stringify(bankInput),
    }),
};

export default api;
